import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { runInAction } from 'mobx';
import { useRelatedProducts } from 'react-instantsearch';
import SliderLinkedItems from '@components/SliderLinkedItems/SliderLinkedItems';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import ProductState from '@features/categories/mobx/state/ProductState';
import { TAlgoliaProductHit } from '@features/categories/typescript';
import { getLanguage } from '@utils/misc';
import getProductUrl from '@utils/getProductUrl';
import { checkAndReturnIfNull } from '@components/Search/utils/helpers';
import styles from '@features/productPreview/sections/RecommendedProducts/RecommendedProducts.styles';
type PropsType = {
  objectIDs: string[];
  limit: number;
};
const hasProductImage = (hit: TAlgoliaProductHit) => Boolean(hit.image || hit.files?.find(file => file.code === 'PRODUCT_VIDEO_PACKSHOT')?.url || hit.files?.find(file => file.code === 'PRODUCT_MAIN_WITH_SHADOW')?.url || hit.files?.find(file => file.code === 'PRODUCT_MAIN')?.url);
const AlgoliaRecommendedProductsContent = ({
  objectIDs,
  limit
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    locale
  } = useRouter();
  const language = getLanguage(locale);
  const {
    items
  } = useRelatedProducts<TAlgoliaProductHit>({
    objectIDs,
    limit
  });
  const products = useMemo(() => {
    const result: ProductState[] = [];
    items.forEach(hit => {
      if (!hasProductImage(hit)) return;
      const productID = hit.id_product;
      runInAction(() => {
        result.push(new ProductState({
          id: productID,
          files: checkAndReturnIfNull(hit.files, null),
          flags: checkAndReturnIfNull(hit.flags, null),
          manufacturer: checkAndReturnIfNull(hit.manufacturer, null),
          name: hit[`name_${language}`],
          slug: getProductUrl(hit[`name_${language}`], productID),
          configurable: checkAndReturnIfNull(hit.configurable, false),
          behaviour: hit.behaviour,
          colorSwatch: checkAndReturnIfNull(hit.colorSwatch, null),
          basePrice: checkAndReturnIfNull(hit.basePrice, null),
          currentPrice: checkAndReturnIfNull(hit.currentPrice, null),
          omnibusPrice: checkAndReturnIfNull(hit.omnibusPrice, null),
          price: checkAndReturnIfNull(hit.price, null),
          priceUnit: checkAndReturnIfNull(hit.priceUnit, null)
        }));
      });
    });
    return result;
  }, [items, language]);
  if (products.length === 0) return null;
  return <ErrorBoundary>
      <section id="collection" sx={styles.section}>
        <SliderLinkedItems products={products} title={t('recommendedProductsTitle')} />
      </section>
    </ErrorBoundary>;
};
export default AlgoliaRecommendedProductsContent;